import React, { useEffect, useRef, useState } from "react";
import { X, ShieldCheck, Sparkles, BrainCircuit, Loader2 } from "lucide-react";
import { postSignal, aiFillSignal } from "../../api";
import { toast } from "sonner";

const emptyErrors = {};

function defaultsFromBid(bid, side = "SELL") {
  const b = Number(bid) || 2650;
  if (side === "BUY") {
    return {
      openLow: (b - 1).toFixed(2), openHigh: (b + 1).toFixed(2), sl: (b - 5).toFixed(2),
      tp1: (b + 7).toFixed(2), tp2: (b + 13).toFixed(2), tp3: (b + 20).toFixed(2), tp4: (b + 30).toFixed(2),
    };
  }
  return {
    openLow: (b - 1).toFixed(2), openHigh: (b + 1).toFixed(2), sl: (b + 5).toFixed(2),
    tp1: (b - 7).toFixed(2), tp2: (b - 13).toFixed(2), tp3: (b - 20).toFixed(2), tp4: (b - 30).toFixed(2),
  };
}

export default function AddSignalDialog({ open, onClose, prefill, marketBid, onSubmitted }) {
  const [form, setForm] = useState({ side: "SELL", volume: "0.20", note: "", ...defaultsFromBid(marketBid, "SELL") });
  const [errors, setErrors] = useState(emptyErrors);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);

  const bidRef = useRef(marketBid);
  bidRef.current = marketBid;

  useEffect(() => {
    if (!open) return;
    const marketBid = bidRef.current;
    setErrors(emptyErrors);
    setAiInfo(null);
    if (prefill) {
      setForm({
        side: prefill.side || "SELL",
        volume: String(prefill.volume ?? "0.20"),
        openLow: String(prefill.openLow ?? ""),
        openHigh: String(prefill.openHigh ?? ""),
        sl: String(prefill.sl ?? ""),
        tp1: prefill.tps?.[0] != null ? String(prefill.tps[0]) : "",
        tp2: prefill.tps?.[1] != null ? String(prefill.tps[1]) : "",
        tp3: prefill.tps?.[2] != null ? String(prefill.tps[2]) : "",
        tp4: prefill.tps?.[3] != null ? String(prefill.tps[3]) : "",
        note: prefill.reason || "",
      });
    } else {
      setForm({ side: "SELL", volume: "0.20", note: "", ...defaultsFromBid(marketBid, "SELL") });
    }
  }, [open, prefill]);

  if (!open) return null;

  const num = (v) => Number(String(v).replace(",", "."));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setSide = (side) => {
    // keep volume/note, regenerate coherent defaults for the chosen side unless user is editing a prefill
    setForm((f) => ({ ...f, side, ...defaultsFromBid(marketBid, side) }));
    setErrors(emptyErrors);
  };

  const fillWithAi = async () => {
    setAiBusy(true);
    try {
      const s = await aiFillSignal();
      setForm({
        side: s.side, volume: String(s.volume), openLow: String(s.openLow), openHigh: String(s.openHigh), sl: String(s.sl),
        tp1: s.tps[0] != null ? String(s.tps[0]) : "", tp2: s.tps[1] != null ? String(s.tps[1]) : "",
        tp3: s.tps[2] != null ? String(s.tps[2]) : "", tp4: s.tps[3] != null ? String(s.tps[3]) : "",
        note: s.reason || "",
      });
      setErrors(emptyErrors);
      setAiInfo(s);
      toast.success(`AI: ${s.side} XAUUSD compilato (${Math.round(s.confidence * 100)}%) · controlla e invia`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI non disponibile");
    } finally {
      setAiBusy(false);
    }
  };

  const validate = () => {
    const e = {};
    const side = form.side;
    const vol = num(form.volume);
    const lo = num(form.openLow);
    const hi = num(form.openHigh || form.openLow);
    const sl = num(form.sl);
    const tps = [form.tp1, form.tp2, form.tp3, form.tp4].filter((t) => String(t).trim() !== "").map(num);

    if (!(vol >= 0.01 && vol <= 1)) e.volume = "Volume tra 0.01 e 1.00 lotti";
    if (!(lo > 0)) e.openLow = "OPEN minimo obbligatorio";
    if (!(hi >= lo)) e.openHigh = "OPEN massimo ≥ OPEN minimo";
    if (!(sl > 0)) e.sl = "Stop Loss obbligatorio";
    if (tps.length < 1) e.tp1 = "Almeno un Take Profit";
    if (new Set(tps).size !== tps.length) e.tp1 = "I TP devono essere unici";

    const mid = (lo + hi) / 2;
    if (side === "SELL") {
      if (sl > 0 && sl <= hi) e.sl = "SELL: SL deve stare SOPRA la zona OPEN";
      tps.forEach((t, i) => { if (t >= lo) e[`tp${i + 1}`] = "SELL: TP sotto la OPEN"; });
      for (let i = 1; i < tps.length; i++) if (tps[i] >= tps[i - 1]) e[`tp${i + 1}`] = "TP decrescenti (SELL)";
    } else {
      if (sl > 0 && sl >= lo) e.sl = "BUY: SL deve stare SOTTO la zona OPEN";
      tps.forEach((t, i) => { if (t <= hi) e[`tp${i + 1}`] = "BUY: TP sopra la OPEN"; });
      for (let i = 1; i < tps.length; i++) if (tps[i] <= tps[i - 1]) e[`tp${i + 1}`] = "TP crescenti (BUY)";
    }
    if (tps.length) {
      const risk = Math.abs(sl - mid);
      const reward = Math.abs(mid - tps[0]);
      if (!risk || reward / risk < 1) e.tp1 = "Primo TP con rapporto R/R ≥ 1:1";
    }
    return { e, tps, vol, lo, hi, sl };
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const { e, tps, vol, lo, hi, sl } = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Controlla i campi evidenziati");
      return;
    }
    setBusy(true);
    try {
      const res = await postSignal({
        symbol: "XAUUSD", side: form.side, volume: vol, openLow: lo, openHigh: hi, sl, tps,
        note: form.note, idempotencyKey: `ui_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      });
      if (res.errors) { toast.error(res.errors.join(" · ")); return; }
      if (res.error) { toast.error(res.error + (res.reasons ? `: ${res.reasons.join(", ")}` : "")); return; }
      toast.success(res.liveQueued ? "Segnale LIVE inviato al Bridge MT5" : "Segnale registrato (paper) e in monitoraggio");
      onSubmitted?.();
      onClose();
    } catch (_) {
      toast.error("Server non raggiungibile: segnale non registrato");
    } finally {
      setBusy(false);
    }
  };

  const rr = (() => {
    const lo = num(form.openLow), hi = num(form.openHigh || form.openLow), sl = num(form.sl), t1 = num(form.tp1);
    const mid = (lo + hi) / 2; const risk = Math.abs(sl - mid); const reward = Math.abs(mid - t1);
    return risk ? (reward / risk).toFixed(2) : "—";
  })();

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid="add-signal-modal-dialog"
        className="w-full max-w-[560px] bg-[#121824] border border-white/12 rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-white/10 sticky top-0 bg-[#121824] z-10">
          <div>
            <h3 className="font-heading text-[16px] font-bold flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400" /> Nuovo Segnale XAUUSD
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Precompilato dal prezzo oro attuale · R/R stimato <b className="text-amber-400">{rr}</b>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 p-1" aria-label="Chiudi"><X size={22} /></button>
        </div>

        <form onSubmit={submit} className="px-5 sm:px-6 py-5 grid gap-4">
          <button type="button" data-testid="ai-fill-all-button" onClick={fillWithAi} disabled={aiBusy || busy}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-[13px] font-bold border border-sky-400/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25 transition-colors disabled:opacity-60">
            {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <BrainCircuit size={15} />}
            {aiBusy ? "L'AI sta analizzando il mercato…" : "Compila Tutto Con AI"}
          </button>
          {aiInfo && (
            <div data-testid="ai-fill-summary" className={`text-[10px] rounded-lg px-3 py-2 border ${aiInfo.fallback ? "border-amber-400/30 bg-amber-500/10 text-amber-200" : "border-sky-400/30 bg-sky-500/10 text-sky-200"}`}>
              {aiInfo.fallback ? "L'AI consiglia di attendere: compilato un setup di riserva dal momentum." : `Compilato dall'AI · ${aiInfo.models} modelli · accordo ${Math.round(aiInfo.agreement * 100)}% · R/R ${aiInfo.rr}`}
              {" "}Puoi modificare i campi prima di inviare.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="signal-side-buy-radio" onClick={() => setSide("BUY")}
              className={`py-3 rounded-lg text-[13px] font-bold border transition-colors ${form.side === "BUY" ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400" : "border-white/10 bg-[#0F1728] text-slate-400 hover:text-slate-200"}`}>
              ▲ BUY · Acquisto
            </button>
            <button type="button" data-testid="signal-side-sell-radio" onClick={() => setSide("SELL")}
              className={`py-3 rounded-lg text-[13px] font-bold border transition-colors ${form.side === "SELL" ? "border-rose-500/50 bg-rose-500/15 text-rose-400" : "border-white/10 bg-[#0F1728] text-slate-400 hover:text-slate-200"}`}>
              ▼ SELL · Vendita
            </button>
          </div>

          <Field label="Volume (lotti)" testid="signal-volume-input" value={form.volume} onChange={set("volume")} placeholder="0.20" error={errors.volume} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="OPEN minimo" testid="signal-open-low-input" value={form.openLow} onChange={set("openLow")} error={errors.openLow} />
            <Field label="OPEN massimo" testid="signal-open-high-input" value={form.openHigh} onChange={set("openHigh")} error={errors.openHigh} />
          </div>

          <Field label="Stop Loss (SL)" testid="signal-sl-input" value={form.sl} onChange={set("sl")} danger error={errors.sl} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Take Profit 1" testid="signal-tp1-input" value={form.tp1} onChange={set("tp1")} good error={errors.tp1} />
            <Field label="Take Profit 2" testid="signal-tp2-input" value={form.tp2} onChange={set("tp2")} good error={errors.tp2} />
            <Field label="Take Profit 3" testid="signal-tp3-input" value={form.tp3} onChange={set("tp3")} good error={errors.tp3} />
            <Field label="Take Profit 4" testid="signal-tp4-input" value={form.tp4} onChange={set("tp4")} good error={errors.tp4} />
          </div>

          <label className="grid gap-1.5">
            <span className="text-[10px] text-slate-400">Note / Strategia</span>
            <textarea value={form.note} onChange={set("note")} rows={2}
              className="bg-[#0A1220] border border-white/12 rounded-lg px-3 py-2 text-[12px] text-slate-100 outline-none focus:border-amber-500/50 resize-none"
              placeholder="Es. struttura ribassista, ingresso a scalare…" />
          </label>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-3 sm:py-2.5 rounded-lg text-[12px] text-slate-300 bg-[#0F1728] border border-white/10 hover:bg-[#182236]">
              Annulla
            </button>
            <button type="submit" data-testid="submit-signal-to-mt5-button" disabled={busy}
              className="flex items-center justify-center gap-1.5 px-5 py-3 sm:py-2.5 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 disabled:opacity-60">
              <ShieldCheck size={14} /> {busy ? "Invio…" : "Invia a MT5"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, good, danger, error }) {
  return (
    <label className="grid gap-1.5">
      <span className={`text-[10px] ${good ? "text-emerald-400/80" : danger ? "text-rose-400/80" : "text-slate-400"}`}>{label}</span>
      <input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder} inputMode="decimal"
        className={`bg-[#0A1220] border rounded-lg px-3 py-2.5 sm:py-2 text-[13px] sm:text-[12px] font-mono text-slate-100 outline-none focus:border-amber-500/50 ${error ? "border-rose-500/70" : "border-white/12"}`} />
      {error && <span data-testid={`${testid}-error`} className="text-[10px] text-rose-400">{error}</span>}
    </label>
  );
}
